import { useEffect, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import parse from "html-react-parser";
import ClickAwayListener from "components/ClickAwayListener";
import { getLocaleString } from "utils/localization/locale";
import "assets/scss/autocomplete.scss";
import get from "lodash/get";

function Autocomplete(props) {
  const [state, setStateHook] = useState({
    activeSuggestion: 0,
    filteredSuggestions: props.suggestions?.length ? props.suggestions : [],
    showSuggestions: false,
    userInput: props.defaultValue || "",
    hide: false,
    typing: false,
    firstTimeFetch: false,
  });
  const { suggestions, getSelected } = props;
  const stateRef = useRef(state);
  const callbacks = useRef([]);
  const previousSuggestions = useRef(suggestions);
  const pendingScroll = useRef(null);

  const setState = (updates, callback) => {
    const nextState = { ...stateRef.current, ...updates };
    stateRef.current = nextState;
    setStateHook(nextState);
    if (callback) callbacks.current.push(callback);
  };

  useLayoutEffect(() => {
    callbacks.current.splice(0).forEach((callback) => callback());
  }, [state]);

  const scrollList = (activeSuggestion) => {
    if (document.querySelector(".suggestion-active")) {
      const ul = document.querySelector(".suggestions");
      const elHeight =
        document.querySelector(".suggestion-active").clientHeight;
      const scrollTop = ul.scrollTop;
      const viewport = scrollTop + ul.clientHeight;
      const elOffset = elHeight * activeSuggestion;
      if (elOffset < scrollTop || elOffset + elHeight > viewport) {
        ul.scrollTop = elOffset;
      }
    }
  };

  useEffect(() => {
    if (previousSuggestions.current === suggestions) return;
    previousSuggestions.current = suggestions;
    const filteredSuggestions = suggestions?.length ? suggestions : [];
    if (stateRef.current.firstTimeFetch && filteredSuggestions[0]) {
      setState({ firstTimeFetch: false, filteredSuggestions: [] }, () =>
        getSelected(filteredSuggestions[0])
      );
    } else {
      setState({ filteredSuggestions });
    }
  }, [suggestions, getSelected]);

  useEffect(() => {
    if (pendingScroll.current !== null) {
      scrollList(pendingScroll.current);
      pendingScroll.current = null;
    }
  }, [state.activeSuggestion]);

  const onChange = (e) => {
    props.search(e.currentTarget.value);
    setState({
      activeSuggestion: 0,
      showSuggestions: true,
      hide: false,
      typing: true,
      userInput: e.currentTarget.value,
    });
  };

  const onClick = (e, item, activeSuggestion) => {
    setState({
      activeSuggestion,
      showSuggestions: false,
      typing: false,
      hide: true,
      userInput: "",
    });
    props.getSelected(item);
  };

  const onDelete = () => {
    setState({
      activeSuggestion: 0,
      showSuggestions: false,
      typing: false,
      hide: true,
      userInput: "",
    });
    props.getSelected(null);
  };

  const onKeyDown = (e) => {
    const { activeSuggestion, filteredSuggestions, typing, userInput } =
      stateRef.current;
    const { defaultValue, getSelected } = props;
    if (e.keyCode === 13 && filteredSuggestions.length) {
      e.preventDefault();
      setState({
        userInput: "",
        activeSuggestion: activeSuggestion || 0,
        showSuggestions: false,
      });
      getSelected(filteredSuggestions[activeSuggestion || 0]);
    } else if (e.keyCode === 13 && !filteredSuggestions.length) {
      e.preventDefault();
      get(props, "includeDiffEmails", false)
        ? getSelected(userInput)
        : getSelected(filteredSuggestions[activeSuggestion || 0]);
      setState({
        userInput: "",
        activeSuggestion: activeSuggestion || 0,
        showSuggestions: false,
      });
    } else if (e.keyCode === 38) {
      const nextActiveSuggestion =
        activeSuggestion === 0 ? activeSuggestion : activeSuggestion - 1;
      pendingScroll.current = nextActiveSuggestion;
      setState({ activeSuggestion: nextActiveSuggestion });
    } else if (e.keyCode === 40) {
      const nextActiveSuggestion =
        activeSuggestion + 1 === filteredSuggestions.length ||
        filteredSuggestions.length === 1
          ? activeSuggestion
          : activeSuggestion + 1;
      pendingScroll.current = nextActiveSuggestion;
      setState({ activeSuggestion: nextActiveSuggestion });
    } else if (e.keyCode === 8 && !typing && defaultValue) {
      e.preventDefault();
      setState({ userInput: "" }, () => getSelected(null));
    }
  };

  const {
    disabled,
    isLoading,
    placeholder,
    defaultValue,
    inputClassName,
    inputWrapperClassName,
    error,
    autoFocus,
    searchStyle,
  } = props;
  const {
    activeSuggestion,
    filteredSuggestions,
    firstTimeFetch,
    showSuggestions,
    userInput,
    hide,
  } = state;

  let suggestionsListComponent;
  if (isLoading && !firstTimeFetch) {
    suggestionsListComponent = (
      <div className="no-suggestions">
        <em>{getLocaleString("common.spinner.loading")}</em>
      </div>
    );
  } else if (showSuggestions && userInput) {
    if (filteredSuggestions.length) {
      suggestionsListComponent = (
        <ul className={cx("suggestions", { hide })}>
          {filteredSuggestions
            .filter((x) => x.name)
            .map((suggestion, index) => {
              const className = cx(
                { "suggestion-active": index === activeSuggestion },
                [suggestion.type]
              );
              return (
                <li
                  className={className}
                  key={suggestion.name + index}
                  onClick={(e) => onClick(e, suggestion, index)}
                >
                  {parse(suggestion.name)}
                </li>
              );
            })}
        </ul>
      );
    } else if (userInput && !isLoading && !filteredSuggestions.length) {
      suggestionsListComponent = (
        <div className="no-suggestions">
          <em>{getLocaleString("common.noData")}</em>
        </div>
      );
    }
  }

  return (
    <ClickAwayListener
      onClickAway={() => setState({ showSuggestions: false, hide: true })}
    >
      <div
        id="search-autocomplete"
        className={cx("autocomplete inputWithIcon", inputWrapperClassName)}
      >
        <input
          type="text"
          onChange={onChange}
          onFocus={() => setState({ hide: false, showSuggestions: true })}
          onKeyDown={onKeyDown}
          value={defaultValue || userInput}
          placeholder={placeholder || ""}
          disabled={disabled}
          autoFocus={autoFocus}
          style={error ? { border: "1px solid red" } : {}}
          className={cx(
            "inputWithIconInput form-control",
            `${!hide && userInput ? "focused-suggestion" : ""}`,
            inputClassName
          )}
        />
        {defaultValue && !firstTimeFetch ? (
          <i
            onClick={onDelete}
            title="Clear"
            className="fas fa-remove cross cursor-pointer text-primary "
            aria-hidden="true"
            style={searchStyle ? searchStyle : { top: 3 }}
          />
        ) : (
          <i
            style={searchStyle ? searchStyle : {}}
            className="fas fa-search search text-primary"
            aria-hidden="true"
          />
        )}
        {suggestionsListComponent}
      </div>
    </ClickAwayListener>
  );
}

export default Autocomplete;
